/* eslint-disable complexity */
/* eslint-disable react-hooks/rules-of-hooks */ // React Hooks must be called in a React function component or a custom React Hook function
import _, { noop } from 'lodash';
import ProgressBar from 'design-system/ProgressBar';
import { makeStyles } from 'design-system/styles';
import React, { useRef, useState } from 'react';
import { fetchApi } from 'react-scale/redux/actions';
import uuid from 'uuid';
import { Button, CircularProgress } from 'design-system';
import TextInput from 'design-system/TextInput';

import usePreventWindowUnload from 'hooks/usePreventWindowUnload';
import { TextCollectionAttachmentType } from '@scale/shared/types/params/textcollection';
import {
  DeepLinkInfo,
  GenericDataCollectionAttachment,
  GenericDataCollectionDataToCollect,
  IngestionActionProgressEvent,
  LiveInstructionSection,
} from '@scale/shared/types/params/genericdatacollection';
import GenericDataCollectionAudioRecorder from 'react-scale/components/scalers/tasks/GenericDataCollection/GenericDataCollectionAudioRecorder';
// import GenericDataCollectionVideoRecorder from 'react-scale/components/scalers/tasks/GenericDataCollection/GenericDataCollectionVideoRecorder';
import { DataInfo, IOnUploadComplete } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useCDSMultipartUpload, {
  CDSMultipartUploadHook,
} from 'react-scale/hooks/useCDSMultipartUpload';
import { captureClientSideError } from 'react-scale/lib/errors';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import useMultipartUpload, { MultipartUploadHook } from 'react-scale/hooks/useMultipartUpload';
import apiFetch from 'react-scale/lib/api-fetch';
import { ContentUnderstandingUploadResponseBody } from '@scale/mm-shared';

interface UploaderProps {
  subtaskId: string;
  assignmentId?: string;
  userId?: string;
  projectId?: string;
  objectCategory?: string;
  attachments: GenericDataCollectionAttachment[];
  dataToCollect: GenericDataCollectionDataToCollect[];
  fileInfos: (DataInfo | undefined)[];
  currentAttachmentIndex: number;
  setSignedDataUrl: (
    signedDataUrl: string,
    attachmentIndex: number,
    dataChunkIndex?: number,
  ) => void;
  setFileInfo: (fileInfo: DataInfo, attachmentIndex: number) => void;
  uploadProgresses: number[][];
  setUploadProgress: (uploadProgress: number, attachmentIndex: number, chunkIndex: number) => void;
  // action is 'append', 'delete',  ideally we should use an enum.
  adjustUploadProgress: (attachmentIndex: number, action: string) => void;
  generateDeepLink?: (
    index: number,
    deepLink: DeepLinkInfo,
    collectorSuppliedVrsTaskIdRegex?: string,
  ) => string | undefined;
  ingestionActionProgresses?: IngestionActionProgressEvent[][];
  liveInstructions?: LiveInstructionSection[];
  fetchExternalAttachmentsOnComplete: (attachmentIndex: number) => void;
  isAttachmentUploadComplete: (attachmentIndex: number) => boolean;
  onUploadComplete: (payload: IOnUploadComplete) => void;
}

const DATA_TYPE_TO_ACCEPT_REGEX: Partial<Record<TextCollectionAttachmentType, string>> = {
  [TextCollectionAttachmentType.Image]: 'image/*',
  [TextCollectionAttachmentType.Video]: 'video/*',
  [TextCollectionAttachmentType.Audio]: 'audio/*',
  [TextCollectionAttachmentType.Text]: 'text/*',
};

const UploadCarousel = (props: UploaderProps) => {
  const styles = useStyles();
  const [collectorSuppliedVrsTaskId, setCollectorSuppliedVrsTaskId] = useState<string>('');
  const sendSamlRequest = async (fileIndex: number): Promise<void> => {
    const url = new URL(window.location.href);
    let host = 'https://www.remotasks.com';
    if (url.hostname === 'localhost') {
      host = 'http://localhost:3000';
    }

    window.location.href = `${host}/internal/dc-saml-auth?fileIndex=${fileIndex}&subtaskId=${props.subtaskId}`;
  };
  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.headerTitle}>
          Upload {props.attachments[props.currentAttachmentIndex].type} (
          {props.attachments[props.currentAttachmentIndex].caption})
          {_.isEmpty(props.objectCategory) ? '' : `of ${props.objectCategory}`}
        </h3>
      </div>
      <div className={styles.uploadField}>
        {props.dataToCollect.map((dataToCollect, dataToCollectIdx) => {
          const hidden = dataToCollectIdx !== props.currentAttachmentIndex;
          const isCollectedInChunks = !!dataToCollect.data_chunks;

          const addDataChunk = () => {
            const newFileInfo = _.cloneDeep(props.fileInfos[dataToCollectIdx]);
            if (!newFileInfo?.dataChunkInfo) return;
            newFileInfo.dataChunkInfo = [..._.cloneDeep(newFileInfo.dataChunkInfo), undefined];
            props.setFileInfo(newFileInfo, dataToCollectIdx);
            props.adjustUploadProgress(dataToCollectIdx, 'append');
          };

          const deleteDataChunk = () => {
            if (
              !window.confirm(
                `Are you sure you want to delete this ${dataToCollect.data_chunks![0].type}?`,
              )
            ) {
              return;
            }
            const newFileInfo = _.cloneDeep(props.fileInfos[dataToCollectIdx]);
            if (!newFileInfo?.dataChunkInfo) return;
            newFileInfo.dataChunkInfo = _.cloneDeep(newFileInfo.dataChunkInfo).slice(
              0,
              newFileInfo.dataChunkInfo.length - 1,
            );
            props.setFileInfo(newFileInfo, dataToCollectIdx);
            props.adjustUploadProgress(dataToCollectIdx, 'remove');
          };

          const regex = dataToCollect.deep_link?.collectorSuppliedVrsTaskIdRegex;
          let validCollectorSuppliedVrsTaskId = false;
          if (regex && new RegExp(regex).test(collectorSuppliedVrsTaskId)) {
            validCollectorSuppliedVrsTaskId = true;
          }
          const samlConfig = dataToCollect.samlConfig;
          const samlAuthUpload = samlConfig &&
            props.currentAttachmentIndex === dataToCollectIdx && (
              <div>
                <Button
                  style={{ marginLeft: 10 }}
                  onClick={() => sendSamlRequest(dataToCollectIdx)}
                  type="secondary"
                >
                  <span style={{ marginRight: 3 }}> Click to Record </span>
                  <FontAwesomeIcon icon="eye" size="lg" />
                </Button>
              </div>
            );
          const deepLinkUploadDisabled =
            dataToCollect.deep_link?.collectorSuppliedVrsTaskIdRegex &&
            !validCollectorSuppliedVrsTaskId;
          const deepLinkUpload = props.generateDeepLink &&
            props.currentAttachmentIndex === dataToCollectIdx &&
            dataToCollect.deep_link && (
              <div>
                <div>
                  <span>{'Upload with deep link'}</span>
                  {props.isAttachmentUploadComplete(dataToCollectIdx) && (
                    <span>
                      <Button
                        style={{ marginLeft: 10 }}
                        onClick={() => props.fetchExternalAttachmentsOnComplete(dataToCollectIdx)}
                        type="secondary"
                      >
                        <span style={{ marginRight: 3 }}> Click to load your videos! </span>
                        <FontAwesomeIcon icon="eye" size="lg" />
                      </Button>
                    </span>
                  )}
                </div>
                <div>
                  <a
                    href={props.generateDeepLink(
                      dataToCollectIdx,
                      dataToCollect.deep_link,
                      collectorSuppliedVrsTaskId,
                    )}
                    className={
                      deepLinkUploadDisabled ? styles.uploadDisabled : styles.uploadEnabled
                    }
                  >
                    Click To Record
                  </a>
                  {dataToCollect.deep_link.collectorSuppliedVrsTaskIdRegex && (
                    <TextInput
                      onChange={e => {
                        setCollectorSuppliedVrsTaskId(e.target.value);
                      }}
                      rows={1}
                      value={collectorSuppliedVrsTaskId}
                      disabled={false}
                      helperText={
                        _.isEmpty(collectorSuppliedVrsTaskId)
                          ? 'Paste the recording ID'
                          : validCollectorSuppliedVrsTaskId
                          ? ''
                          : 'Invalid recording ID, paste again'
                      }
                      error={!validCollectorSuppliedVrsTaskId}
                    />
                  )}
                </div>
              </div>
            );
          const uploadInterface = (
            <div key={`${dataToCollectIdx}`} className={hidden ? styles.hidden : styles.visible}>
              {deepLinkUpload}
              {samlAuthUpload}
              {(
                props.fileInfos[dataToCollectIdx]?.dataChunkInfo ?? [
                  props.fileInfos?.[dataToCollectIdx],
                ]
              ).map((dataChunk, dataChunkIdx) => {
                const dataChunkInfo =
                  dataToCollect.data_chunks?.[dataChunkIdx] ??
                  dataToCollect.data_chunks?.[0] ??
                  dataToCollect;

                const progress = _.get(props.uploadProgresses[dataToCollectIdx], dataChunkIdx);
                const progressStr = (progress ?? 0).toFixed(4);
                const setDataChunkInfo = (dataChunkInfo: DataInfo) => {
                  if (isCollectedInChunks) {
                    let newFileInfo = _.cloneDeep(props.fileInfos[dataToCollectIdx]);
                    if (!newFileInfo) {
                      newFileInfo = {
                        unsignedDataUrl: '',
                        fileId: '',
                        contentType: '',
                      };
                    }
                    if (!newFileInfo.dataChunkInfo) {
                      newFileInfo.dataChunkInfo = _.times(
                        dataToCollect.data_chunks!.length,
                        _.constant(undefined),
                      );
                    }
                    newFileInfo.dataChunkInfo[dataChunkIdx] = dataChunkInfo;
                    props.setFileInfo(newFileInfo, dataToCollectIdx);
                  } else {
                    props.setFileInfo(dataChunkInfo, dataToCollectIdx);
                  }
                };

                const setSignedDataUrl = (signedDataUrl: string) => {
                  if (isCollectedInChunks) {
                    props.setSignedDataUrl(signedDataUrl, dataToCollectIdx, dataChunkIdx);
                  } else {
                    props.setSignedDataUrl(signedDataUrl, dataToCollectIdx);
                  }
                };

                const setUploadProgress = (uploadProgress: number) => {
                  props.setUploadProgress(uploadProgress, dataToCollectIdx, dataChunkIdx);
                };

                const showDataChunk = () => {
                  const signedUrl = isCollectedInChunks
                    ? props.fileInfos?.[dataToCollectIdx]?.dataChunkInfo?.[dataChunkIdx]
                        ?.signedDataUrl
                    : props.fileInfos?.[dataToCollectIdx]?.signedDataUrl;
                  props.setSignedDataUrl(
                    signedUrl ?? '',
                    dataToCollectIdx,
                    isCollectedInChunks ? dataChunkIdx : undefined,
                  );
                };
                const ingestionActionProgressData =
                  props.ingestionActionProgresses?.[dataToCollectIdx]?.[dataChunkIdx];

                return (
                  <>
                    <div style={{ padding: '8px', paddingTop: '16px' }}>
                      {isCollectedInChunks && (
                        <div>
                          <Button type="info" onClick={showDataChunk}>
                            Show {`${dataChunkInfo.name} - #${dataChunkIdx + 1}`}
                          </Button>
                        </div>
                      )}
                      {!dataToCollect.deep_link && (
                        <MultipartFileUpload
                          key={`${dataToCollectIdx}-${dataChunkIdx}`}
                          projectId={props.projectId}
                          dataToCollect={dataChunkInfo}
                          userId={props.userId}
                          assignmentId={props.assignmentId}
                          setSignedDataUrl={setSignedDataUrl}
                          setFileInfo={setDataChunkInfo}
                          fileInfo={dataChunk}
                          uploadProgress={progress}
                          setUploadProgress={setUploadProgress}
                          onUploadComplete={props.onUploadComplete}
                          fileIndex={dataToCollectIdx}
                          fileChunkIndex={dataChunkIdx}
                          isCollectedInChunks={isCollectedInChunks}
                        />
                      )}
                    </div>
                    <div>
                      <ProgressBar completed={progress} total={100} animation={true} />
                    </div>
                    <div>
                      {' '}
                      <b> Progress: </b> <i> {progressStr} / 100.0000 </i>
                    </div>
                    {ingestionActionProgressData && (
                      <div>
                        <b> Details: </b>
                        <span
                          className={
                            ingestionActionProgressData.success
                              ? ingestionActionProgressData.stage ===
                                ingestionActionProgressData.maxStages
                                ? styles.successMessage
                                : styles.inProgressMessage
                              : styles.failureMessage
                          }
                        >
                          <b>{ingestionActionProgressData.message}</b>
                        </span>
                      </div>
                    )}
                    <div className={styles.auxiliaryRecordingMethods}>
                      {dataToCollect.type === TextCollectionAttachmentType.Audio && (
                        <GenericDataCollectionAudioRecorder
                          userId={props.userId}
                          assignmentId={props.assignmentId}
                          dataToCollect={dataChunkInfo}
                          setSignedDataUrl={setSignedDataUrl}
                          setFileInfo={setDataChunkInfo}
                          uploadProgress={progress}
                          setUploadProgress={setUploadProgress}
                          getPreprocessFn={getPreprocessFn}
                          onUploadComplete={props.onUploadComplete}
                          fileIndex={dataToCollectIdx}
                          fileChunkIndex={dataChunkIdx}
                        />
                      )}
                      {/* {dataToCollect.type === TextCollectionAttachmentType.Video &&
                        dataToCollect.allowInBrowserRecording && (
                          <GenericDataCollectionVideoRecorder
                            userId={props.userId}
                            assignmentId={props.assignmentId}
                            dataToCollect={dataChunkInfo}
                            setSignedDataUrl={setSignedDataUrl}
                            setFileInfo={setDataChunkInfo}
                            uploadProgress={progress}
                            setUploadProgress={setUploadProgress}
                            getPreprocessFn={getPreprocessFn}
                            liveInstructions={dataToCollect.liveInstructions}
                            videoWidth={dataToCollect.requirements?.width_pixels?.min}
                            videoHeight={dataToCollect.requirements?.height_pixels?.min}
                            onUploadComplete={props.onUploadComplete}
                            fileIndex={dataToCollectIdx}
                            fileChunkIndex={dataChunkIdx}
                          />
                        )} */}
                    </div>
                  </>
                );
              })}
              {dataToCollect.concatenate_chunks &&
                dataToCollect.data_chunks &&
                props.fileInfos[dataToCollectIdx]?.dataChunkInfo && (
                  <div style={{ padding: '4px', paddingTop: '12px' }}>
                    <span style={{ padding: '4px' }}>
                      <Button onClick={addDataChunk} type="primary">
                        Add {dataToCollect.data_chunks[0].type}
                      </Button>
                    </span>
                    {props.fileInfos[dataToCollectIdx]!.dataChunkInfo!.length > 1 && (
                      <span style={{ padding: '4px' }}>
                        <Button onClick={deleteDataChunk} type="error">
                          Delete last {dataToCollect.data_chunks![0].type}
                        </Button>
                      </span>
                    )}
                  </div>
                )}
            </div>
          );

          return uploadInterface;
        })}
      </div>
    </>
  );
};

export interface FileUploadProps {
  projectId?: string;
  assignmentId?: string;
  dataToCollect: GenericDataCollectionDataToCollect;
  userId?: string;
  setSignedDataUrl: (signedUploadUrl: string) => void;
  setFileInfo: (fileInfo: DataInfo) => void;
  fileInfo?: DataInfo;
  uploadProgress: number;
  setUploadProgress: (uploadProgress: number) => void;
  onUploadComplete: (payload: IOnUploadComplete) => void;
  fileIndex: number;
  fileChunkIndex?: number;
  isCollectedInChunks?: boolean;
}

export const MultipartFileUpload = (props: FileUploadProps) => {
  const fileInputEl = useRef<HTMLInputElement>(null);
  const [isWaitingForValidation, setIsWaitingForValidation] = useState<boolean>(false);
  const [IsWaitingForFileUpload, setIsWaitingForFileUpload] = useState<boolean>(false);
  // Prompt when refreshing/navigating during upload in case it's accidental
  usePreventWindowUnload(
    props.uploadProgress !== 0 && props.uploadProgress !== 100,
    'Are you sure you want to leave the page? Your data has not finished uploading.',
  );

  const shouldUseS3UploadHook = useUnleashFeatureFlag(
    'dc-use-s3-multipart-upload',
    { projectId: props.projectId },
    false,
  );

  const uploadToSharedBucket = useUnleashFeatureFlag(
    'files-upload-shared-bucket',
    { userId: props.userId },
    false,
  );
  const cdsMultipartUploadHook: CDSMultipartUploadHook = useCDSMultipartUpload(
    props.assignmentId,
    props.userId,
  );
  const s3MultipartUploadHook: MultipartUploadHook = useMultipartUpload(
    props.assignmentId,
    props.userId,
  );
  const preprocess = getPreprocessFn(props.dataToCollect);

  const handleFileInputChange = () => {
    if (fileInputEl.current?.files?.[0]) {
      preprocess(fileInputEl.current.files[0], async (processedFile: File) => {
        const fileId = props.fileInfo?.fileId || uuid.v4();
        setIsWaitingForFileUpload(true);
        try {
          // S3 Upload
          if (shouldUseS3UploadHook) {
            const unsignedDataUrl = s3MultipartUploadHook.upload({
              name: fileId,
              file: processedFile,
              onProgress,
              onComplete: onS3UploadComplete,
              onError,
            });

            props.setFileInfo({
              unsignedDataUrl,
              unsignedS3DataUrl: '',
              fileId,
              contentType: processedFile.type,
            });
          } else if (uploadToSharedBucket) {
            const formData = new FormData();
            formData.append('asset', processedFile);
            formData.append('assignmentId', props.assignmentId ?? '');
            const key = 'test';
            const uploadResponse = await apiFetch<ContentUnderstandingUploadResponseBody>(
              null,
              `internal/contentUnderstanding/fileUpload/${key}`,
              {
                method: 'POST',
                body: formData,
              },
            );
            if (uploadResponse.type === 'error') {
              throw new Error(uploadResponse.errors.join(', '));
            }
            const { url, s3Url } = uploadResponse;
            props.setFileInfo({
              unsignedDataUrl: url,
              unsignedS3DataUrl: s3Url,
              fileId,
              contentType: processedFile.type,
            });
          } else {
            const unsignedDataUrl = await cdsMultipartUploadHook.upload({
              name: fileId,
              file: processedFile,
              onProgress,
              onComplete: noop,
              onError,
            });

            props.onUploadComplete({
              signedDataUrl: await validateAndFetchSignedUrl(unsignedDataUrl),
              unsignedDataUrl,
              unsignedS3DataUrl: '',
              fileId,
              contentType: processedFile.type,
              dataIndex: props.fileIndex,
              dataChunkIndex: props.isCollectedInChunks ? props.fileChunkIndex : undefined,
            });
          }
        } catch (e) {
          onError(
            `Error occured while uploading - please try again. If the problem persists, contact support. Error is ${e}`,
          );
          captureClientSideError(
            new Error('Error occurred during frontend GenericDataCollectionAttempt uploading.'),
            'genericdatacollection',
            {
              subtaskId: props.assignmentId,
              userId: props.userId,
              e,
            },
          );
        } finally {
          setIsWaitingForFileUpload(false);
        }
      });
    }
  };

  const onS3UploadComplete = async (_xhr: any, awsObjectKey: string) => {
    if (!(await validatePostUploadRequirements(awsObjectKey))) {
      onError('');
      return;
    }

    props.setUploadProgress(100);

    const body = await fetchApi(`/multipartupload/getSignedUrl`, {
      body: {
        awsObjectKey,
        subtaskId: props.assignmentId,
      },
      maxRetries: 3,
      method: 'POST',
      parseAndHandle: true,
      redirectOnUnauthorized: true,
    });

    props.setSignedDataUrl(body.signedDataUrl);
  };

  const onProgress = (percent: number) => {
    props.setUploadProgress(percent * 100);
  };

  const validateAndFetchSignedUrl = async (cdsUrl: string) => {
    if (!(await validatePostUploadRequirements(cdsUrl))) {
      onError('');
      return;
    }

    props.setUploadProgress(100);

    const body = await fetchApi(`/multipartupload/getSignedCDSUrl`, {
      body: {
        cdsUrl,
        subtaskId: props.assignmentId,
      },
      maxRetries: 3,
      method: 'POST',
      parseAndHandle: true,
      redirectOnUnauthorized: true,
    });

    return body.signedDataUrl;
  };

  const onError = (_message: string) => {
    props.setUploadProgress(0);
    props.setSignedDataUrl('');
    alert(_message);
  };

  const validatePostUploadRequirements = async (cdsUrlOrS3KeySuffix: string) => {
    setIsWaitingForValidation(true);
    // TODO: Add more validations
    const isValid = await validateFPSRequirement(cdsUrlOrS3KeySuffix);
    setIsWaitingForValidation(false);
    return isValid;
  };

  const validateFPSRequirement = async (cdsUrlOrS3KeySuffix: string): Promise<boolean> => {
    if (props.dataToCollect.type !== TextCollectionAttachmentType.Video) return true;

    const fpsRequirements = props.dataToCollect?.requirements?.frames_per_second;

    if (!fpsRequirements) return true;
    const body = await fetchApi(`/genericdatacollection/get_attachment_metadata`, {
      body: {
        cdsUrlOrS3KeySuffix,
        subtaskId: props.assignmentId,
      },
      maxRetries: 3,
      method: 'POST',
      parseAndHandle: true,
      redirectOnUnauthorized: true,
    });

    const attachmentMetadata = body.attachmentMetadata;

    if (!attachmentMetadata) {
      alert(`Video metadata unable to be determined.`);
      return false;
    }

    const fps = attachmentMetadata.video.fps;

    const { min, max } = fpsRequirements;
    const passed = (_.isNil(min) || min <= fps) && (_.isNil(max) || max >= fps);

    if (!passed) {
      alert(
        `FPS of uploaded video does not satisfy requirements. ${
          min ? `Min FPS Req: ${min} |` : ''
        } ${max ? `Max FPS Req: ${max} |` : ''} Uploaded Video FPS: ${fps}`,
      );
    }

    return passed;
  };

  return (
    <>
      <input
        ref={fileInputEl}
        type="file"
        accept={DATA_TYPE_TO_ACCEPT_REGEX[props.dataToCollect.type] ?? ''}
        onChange={handleFileInputChange}
        disabled={shouldUseS3UploadHook ? !s3MultipartUploadHook.initialized : false}
      />
      {(isWaitingForValidation || IsWaitingForFileUpload) && <CircularProgress />}
    </>
  );
};

const getPreprocessFn = (dataToCollect: GenericDataCollectionDataToCollect) => {
  return (file: File, next: any) => validateUploadedData(dataToCollect, file, next, true, () => {});
};

export const validateUploadedData = (
  dataToCollect: GenericDataCollectionDataToCollect,
  file: File,
  next: any,
  shouldAlert: boolean,
  callback: (isValid: boolean, validationMessage?: string) => void,
) => {
  const validationMessages: string[] = [];
  const requirements = dataToCollect.requirements;
  if (!requirements) {
    next(file);
    return callback(true);
  }

  const validateSharedValidations = (file: File) => {
    const { filename } = requirements;
    // validation file name matches provided regex
    if (filename) {
      const { regex, display_regex, extension } = filename;
      if (regex && !new RegExp(regex).test(file.name)) {
        validationMessages.push(
          constructFilenameValidationMessage(dataToCollect.name, file.name, regex, display_regex),
        );
      }
      if (extension) {
        const fileExtension = _.last(file.name.split('.'));
        if (fileExtension !== extension) {
          const validationMessage = `The file extension of the upload for ${dataToCollect.name} needs to be ${extension} but was ${fileExtension}.`;
          validationMessages.push(validationMessage);
        }
      }
    }
  };

  const validateVideoUpload = (file: File) => {
    if (!file.type.startsWith('video')) {
      alert('Selected file is not a video file!');
      return;
    }

    const videoEl = document.createElement('video');
    videoEl.src = window.URL.createObjectURL(file);
    videoEl.onloadedmetadata = _event => {
      window.URL.revokeObjectURL(videoEl.src);
      const { videoWidth, videoHeight, duration } = videoEl;

      const { width_pixels, height_pixels, duration_seconds, orientation } = requirements;

      if (videoWidth === 0 && videoHeight === 0 && shouldAlert) {
        alert(
          `The following message is NOT an error.\nIf you think your video is correct, please ignore this message :)\n\nVideo ${file.name} (the upload for ${dataToCollect.name}) is encoded in HEVC format so we are not able to validate the video upload - please make sure that you're recording with the correct settings (full HD if required, for example).\n\nRefer to https://apple.stackexchange.com/questions/409977/where-to-view-photo-and-movie-metadata-on-ios/409992#409992 (iPhone), https://thetechhacker.com/2018/10/29/how-to-check-video-resolution-and-frame-rate-on-windows-10/ (Windows computer), or https://www.wikihow.com/Check-the-Quality-of-a-Video(Mac) (Mac computer) to find out how to check the width/height of the video before uploading.`,
        );
      } else {
        validateSize(videoWidth, videoHeight, width_pixels, height_pixels);
      }

      validateDuration(duration, duration_seconds);
      validateOrientation(videoWidth, videoHeight, orientation);
      alertOrUpload();
    };
  };

  const validateAudioUpload = (file: File) => {
    if (!file.type.startsWith('audio')) {
      alert('Selected file is not an audio file!');
      return;
    }

    const audioEl = document.createElement('audio');
    audioEl.src = window.URL.createObjectURL(file);
    audioEl.onloadedmetadata = _event => {
      window.URL.revokeObjectURL(audioEl.src);
      const { duration } = audioEl;

      const { duration_seconds } = requirements;

      validateDuration(duration, duration_seconds);
      alertOrUpload();
    };
  };

  const validateImageUpload = (file: File) => {
    if (!file.type.startsWith('image')) {
      alert('Selected file is not an image file!');
      return;
    }

    const imageEl = document.createElement('img');
    imageEl.src = window.URL.createObjectURL(file);
    imageEl.onload = _event => {
      window.URL.revokeObjectURL(imageEl.src);
      const { width, height } = imageEl;

      const { width_pixels, height_pixels, orientation } = requirements;

      validateSize(width, height, width_pixels, height_pixels);
      validateOrientation(width, height, orientation);
      alertOrUpload();
    };
  };

  const validateFileUpload = () => {
    // TODO: add generic file validations here. Shared validations already cover filename and extension.
    alertOrUpload();
  };

  const validateSize = (
    width: number,
    height: number,
    width_pixels?: { min?: number; max?: number },
    height_pixels?: { min?: number; max?: number },
  ) => {
    if (width_pixels) {
      const { min, max } = width_pixels;
      if ((!_.isNil(min) && min > width) || (!_.isNil(max) && max < width)) {
        validationMessages.push(
          constructValidationMessage(dataToCollect.name, 'width', 'px', width, min, max),
        );
      }
    }

    if (height_pixels) {
      const { min, max } = height_pixels;
      if ((!_.isNil(min) && min > height) || (!_.isNil(max) && max < height)) {
        validationMessages.push(
          constructValidationMessage(dataToCollect.name, 'height', 'px', height, min, max),
        );
      }
    }
  };

  const validateDuration = (
    duration: number,
    duration_seconds?: { min?: number; max?: number },
  ) => {
    if (duration_seconds) {
      const { min, max } = duration_seconds;
      if ((!_.isNil(min) && min > duration) || (!_.isNil(max) && max < duration)) {
        validationMessages.push(
          constructValidationMessage(dataToCollect.name, 'length', 'seconds', duration, min, max),
        );
      }
    }
  };

  const validateOrientation = (
    width: number,
    height: number,
    orientation?: { require_portrait?: boolean; require_landscape?: boolean },
  ) => {
    if (orientation) {
      const { require_portrait, require_landscape } = orientation;
      if ((require_portrait && width > height) || (require_landscape && width < height)) {
        validationMessages.push(
          constructOrientationValidationMessage(
            dataToCollect.name,
            require_portrait && width > height ? 'portrait' : 'landscape',
            height,
            width,
          ),
        );
      }
    }
  };

  const alertOrUpload = (): void => {
    const validationMessage = validationMessages.join('\n');
    if (_.isEmpty(validationMessage)) {
      next(file);
      callback(true);
    } else {
      if (shouldAlert) alert(validationMessage);
      callback(false, validationMessage);
    }
  };

  validateSharedValidations(file);
  if (dataToCollect.type === TextCollectionAttachmentType.Video) {
    validateVideoUpload(file);
  } else if (dataToCollect.type === TextCollectionAttachmentType.Audio) {
    validateAudioUpload(file);
  } else if (dataToCollect.type === TextCollectionAttachmentType.Image) {
    validateImageUpload(file);
  } else {
    validateFileUpload();
  }
};

const constructValidationMessage = (
  dataName: string,
  dimensionName: string,
  dimensionMetric: string,
  dimensionValue: number,
  min?: number,
  max?: number,
) => {
  return `The ${dimensionName} of the upload for ${dataName} is ${dimensionValue}${dimensionMetric}, but ${
    !_.isNil(min) ? `a min ${dimensionName} of ${min}${dimensionMetric}` : ''
  }${!_.isNil(min) && !_.isNil(max) ? ' and ' : ''}${
    !_.isNil(max) ? `a max ${dimensionName} of ${max}${dimensionMetric}` : ''
  } is required.`;
};

const constructOrientationValidationMessage = (
  dataName: string,
  orientationValue: 'landscape' | 'portrait',
  height: number,
  width: number,
) => {
  return `The orientation of the upload for ${dataName} needs to be ${orientationValue}, but the height of ${height}px is ${
    orientationValue === 'landscape' ? 'greater than' : 'less than'
  } the width of ${width}px.`;
};

const constructFilenameValidationMessage = (
  dataName: string,
  filename: string,
  regex: string,
  displayRegex: boolean = false, // if this is true, the regex will be displayed in the error message
) => {
  return `The filename ${filename} of the upload for ${dataName} ${
    displayRegex ? 'needs to match the following format but does not: ' + regex : 'is invalid'
  }.`;
};

const useStyles = makeStyles({
  hidden: {
    display: 'none',
  },
  visible: {},
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 16px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: 500,
  },
  uploadField: {
    paddingLeft: 16,
    paddingRight: 32,
  },
  fileField: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  successMessage: {
    color: '#00cc00',
  },
  inProgressMessage: {
    color: '#ff9900',
  },
  failureMessage: {
    color: '#ff0000',
  },
  auxiliaryRecordingMethods: {
    paddingTop: 20,
  },
  uploadDisabled: {
    pointerEvents: 'none',
    color: 'grey',
    paddingRight: 12,
  },
  uploadEnabled: {
    pointerEvents: 'auto',
    paddingRight: 12,
  },
});

export default UploadCarousel;
